import { getScreenWidth } from '../../utils/dom'
import getDirection from './getDirection'

const distance = 10
const bodyPadding = 10
const minArrowPadding = 5
const arrowSize = 15

/**
 * cross browser scroll positions
 */

function getScrollTop() {
  return (
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    document.body.scrollTop ||
    0
  )
}

function getScrollLeft() {
  return (
    window.pageXOffset ||
    document.documentElement.scrollLeft ||
    document.body.scrollLeft ||
    0
  )
}

/**
 * Sets tip max width safely for mobile
 */
function getTipMaxWidth() {
  return typeof document !== 'undefined'
    ? getScreenWidth() - bodyPadding * 2
    : 1000
}

/**
 * Gets wrapper's left position for top/bottom tooltips as well as needed width restriction
 */
export function getUpDownPosition(
  tip: any,
  target: any,
  state: any,
  direction: any,
  fixed?: boolean,
) {
  let left = -10000000
  let top
  let bottom = ''

  if (tip && state.showTip) {
    // get wrapper left position
    const targetRect = target.getBoundingClientRect()
    const targetLeft = targetRect.left + getScrollLeft()

    const halfTargetWidth = Math.round(target.offsetWidth / 2)

    const tipWidth = Math.min(getTipMaxWidth(), tip.offsetWidth)

    // default is centered, but must be higher than body padding
    left = Math.max(
      targetLeft + halfTargetWidth - Math.round(tipWidth / 2),
      bodyPadding + getScrollLeft(),
    )

    // check for right overhang
    const rightOverhang = left + tipWidth + bodyPadding - getScreenWidth()
    if (rightOverhang > 0) {
      left -= rightOverhang
      bottom = 'unset'
    }

    if (direction === 'up') {
      top = targetRect.top + getScrollTop() - (tip.offsetHeight + distance)
      bottom = 'unset'
    } else {
      top = targetRect.bottom + getScrollTop() + distance
      bottom = 'unset'
    }
  }

  return {
    left,
    top,
    bottom,
  }
}

/**
 * gets top position for left/right arrows
 */
function getLeftRightPosition(
  tip: any,
  target: any,
  state: any,
  direction: any,
  fixed?: boolean,
) {
  let left = -10000000
  let top = 0
  let bottom = 'unset'

  if (tip && state.showTip) {
    const scrollTop = fixed ? 0 : getScrollTop()
    const targetRect = target.getBoundingClientRect()
    const targetTop = targetRect.top + scrollTop
    const halfTargetHeight = Math.round(target.offsetHeight / 2)

    // default to middle, but don't go below body
    top = Math.max(
      targetTop + halfTargetHeight - Math.round(tip.offsetHeight / 2),
      bodyPadding + scrollTop,
    )

    // make sure it doesn't go below the arrow
    const arrowTop = targetTop + halfTargetHeight - arrowSize
    top = Math.min(top, arrowTop - minArrowPadding)

    // check for bottom overhang
    const bottomOverhang =
      top - scrollTop + tip.offsetHeight + bodyPadding - window.innerHeight
    if (bottomOverhang > 0) {
      // try to add the body padding below the tip, but don't offset too far from the arrow
      const arrowBottom =
        targetRect.top + scrollTop + halfTargetHeight + arrowSize
      top = Math.max(
        top - bottomOverhang,
        arrowBottom + minArrowPadding - tip.offsetHeight,
      )
      bottom = 'unset'
    }

    if (direction === 'right') {
      left = targetRect.right + distance
      bottom = 'unset'
    } else {
      left = targetRect.left - distance - tip.offsetWidth
      bottom = 'unset'
    }
  }

  return {
    left,
    top,
    bottom,
  }
}

/**
 * sets the Arrow styles based on direction
 */
function getArrowStyles(
  target: any,
  tip: any,
  direction: any,
  state: any,
  props: any,
) {
  if (!target) {
    return {
      top: '0',
      left: '-10000000px',
    }
  }

  const targetRect = target.getBoundingClientRect()
  const halfTargetHeight = Math.round(target.offsetHeight / 2)
  const halfTargetWidth = Math.round(target.offsetWidth / 2)
  const scrollTop = getScrollTop()
  const scrollLeft = getScrollLeft()

  switch (direction) {
    case 'right':
      return {
        top:
          state.showTip && tip
            ? targetRect.top + scrollTop + halfTargetHeight - arrowSize
            : '-10000000px',
        left: targetRect.right + scrollLeft,
        borderRight:
          props.background !== '' ? `10px solid ${props.background}` : '',
        borderTop: '10px solid transparent',
        borderBottom: '10px solid transparent',
      }

    case 'left':
      return {
        top:
          state.showTip && tip
            ? targetRect.top + scrollTop + halfTargetHeight - arrowSize
            : '-10000000px',
        left: targetRect.left + scrollLeft - distance - 1,
        borderLeft:
          props.background !== '' ? `10px solid ${props.background}` : '',
        borderTop: '10px solid transparent',
        borderBottom: '10px solid transparent',
      }

    case 'up':
      return {
        left:
          state.showTip && tip
            ? targetRect.left + scrollLeft + halfTargetWidth - arrowSize
            : '-10000000px',
        top: targetRect.top + scrollTop - distance,
        borderTop:
          props.background !== '' ? `10px solid ${props.background}` : '',
        borderLeft: '10px solid transparent',
        borderRight: '10px solid transparent',
      }

    case 'down':
    default:
      return {
        left:
          state.showTip && tip
            ? targetRect.left + scrollLeft + halfTargetWidth - arrowSize
            : '-10000000px',
        top: targetRect.bottom + scrollTop,
        borderBottom:
          props.background !== '' ? `10px solid ${props.background}` : '',
        borderLeft: '10px solid transparent',
        borderRight: '10px solid transparent',
      }
  }
}

export default function positions(
  direction: any,
  tip: any,
  target: any,
  state: any,
  props: any,
  fixed?: boolean,
) {
  const realDirection = getDirection(
    direction,
    tip,
    target,
    distance,
    bodyPadding,
  )
  const maxWidth = 480

  const tipPosition =
    realDirection === 'up' || realDirection === 'down'
      ? getUpDownPosition(tip, target, state, realDirection, fixed)
      : getLeftRightPosition(tip, target, state, realDirection, fixed)

  return {
    tip: {
      ...tipPosition,
      maxWidth,
    },
    arrow: getArrowStyles(target, tip, realDirection, state, props),
    realDirection,
  }
}
